import {
  ErrorBoundary,
  SectionContainer,
  SectionContainerTypes,
  Skeleton,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Area,
  Bar,
  Cell,
  ComposedChart,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { fundraisingAnalysisQuery } from '../../../../store/home/capture/selector';

import { FundraisingAnalysisLegend } from './components/FundraisingAnalysisChartLegend';
import { FundraisingAnalysisTooltip } from './components/FundraisingAnalysisChartTooltip';
import { FundraisingAnalysisChartYTick } from './components/FundraisingAnalysisChartYTick';

const axisDefaultConfig = {
  fontFamily: Typography['fontfamily.base.regular'],
  fontSize: Typography['fontsize.sm'],
  tickLine: false,
  axisLine: false,
};

const chartTitle = 'ANÁLISE DE CAPTAÇÃO';

export namespace FundraisingAnalysisTypes {
  export interface Props {
    isMobile?: boolean;
  }
}

export const FundraisingAnalysis: React.FC<FundraisingAnalysisTypes.Props> = ({ isMobile }) => {
  const { data, loading, error, refresher } = useRecoilQuery(fundraisingAnalysisQuery);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);

  const handleSelectIndex = useCallback(
    (index: number) => {
      if (index === selectedIndex) {
        setSelectedIndex(undefined);
        return;
      }

      setSelectedIndex(index);
    },
    [selectedIndex]
  );

  const nameMap = useMemo(
    () =>
      new Map([
        ['contributionValue', 'APORTE'],
        ['withdrawValue', 'RESGATE'],
        ['netFundraisingValue', 'CAPTAÇÃO LÍQUIDA'],
      ]),
    []
  );

  const filterOptions: SectionContainerTypes.FilterOption[] = [
    {
      label: 'YTD',
      value: '365',
    },
  ];

  const barCategoryGap = useBreakpointValue({
    base: '10%',
    sm: '15%',
    lg: '30%',
  });

  const chart = useMemo(() => {
    if (loading) {
      return (
        <VStack space={Spacing['stack.xxxs']}>
          <HStack space={Spacing['stack.xxxs']}>
            <Box flex={1}>
              <Skeleton borderRadius="full" maxWidth="295px" height="18px" />
            </Box>
            <HStack space={Spacing['stack.xxxs']} flex={1} justifyContent="flex-end">
              <Skeleton borderRadius="full" flex={1} maxWidth="176px" height="18px" />
              <Skeleton borderRadius="full" flex={1} maxWidth="176px" height="18px" />
            </HStack>
          </HStack>
          <HStack justifyContent="space-between">
            {Array(12)
              .fill(0)
              .map((_, index) => (
                <Skeleton height="265px" borderRadius="full" width="20px" key={index.toString()} />
              ))}
          </HStack>
        </VStack>
      );
    }

    if (error) {
      return <ErrorBoundary onRefresh={refresher} />;
    }

    const barRadius: [number, number, number, number] = [
      Utils.Token.ToInt(BoxRadius, 'xs'),
      Utils.Token.ToInt(BoxRadius, 'xs'),
      0,
      0,
    ];

    return (
      <ResponsiveContainer width="100%" height={344}>
        <ComposedChart data={data?.chartData || []} barGap={10} barCategoryGap={barCategoryGap}>
          <defs>
            <linearGradient id="assetCaptureFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#53BA2138" stopOpacity={0.78} />
              <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey={isMobile ? 'referenceDateShortNormalized' : 'referenceDateNormalized'}
            {...axisDefaultConfig}
          />
          <YAxis {...axisDefaultConfig} hide={isMobile} tick={<FundraisingAnalysisChartYTick />} />
          <ReferenceLine
            y={0}
            stroke={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.5)}
          />
          <Legend
            verticalAlign={isMobile ? 'bottom' : 'top'}
            align="right"
            content={
              <FundraisingAnalysisLegend
                nameMap={nameMap}
                total={data?.total || 0}
                isMobile={isMobile}
              />
            }
          />
          <Tooltip
            content={({ active, label, payload }) => (
              <FundraisingAnalysisTooltip
                label={label}
                active={active}
                payload={payload}
                nameMap={nameMap}
              />
            )}
          />
          <Bar
            type="monotone"
            dataKey="contributionValue"
            stackId="contributionStack"
            // maxBarSize={maxBarSize}
            radius={barRadius}
            fill={Color['brand.tertiary.principal']}
            onClick={(_, index) => handleSelectIndex(index)}>
            {data?.chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={Color['brand.tertiary.principal']}
                fillOpacity={selectedIndex === undefined || selectedIndex === index ? 1 : 0.6}
              />
            ))}
          </Bar>
          <Bar
            type="monotone"
            dataKey="contributionValueStack"
            fill={Color['brand.tertiary.lightest']}
            stackId="contributionStack"
            // maxBarSize={maxBarSize}
            radius={barRadius}
            onClick={(_, index) => handleSelectIndex(index)}>
            {data?.chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={Color['brand.tertiary.lightest']}
                fillOpacity={selectedIndex === undefined || selectedIndex === index ? 1 : 0.6}
              />
            ))}
          </Bar>
          <Bar
            type="monotone"
            dataKey="withdrawValue"
            stackId="withdrawalStack"
            // maxBarSize={maxBarSize}
            radius={barRadius}
            fill={Color['brand.tertiary.light']}
            onClick={(_, index) => handleSelectIndex(index)}>
            {data?.chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={Color['brand.tertiary.light']}
                fillOpacity={selectedIndex === undefined || selectedIndex === index ? 1 : 0.6}
              />
            ))}
          </Bar>
          <Bar
            type="monotone"
            dataKey="withdrawValueStack"
            fill={Color['brand.tertiary.lightest']}
            stackId="withdrawalStack"
            // maxBarSize={maxBarSize}
            radius={barRadius}
            onClick={(_, index) => handleSelectIndex(index)}>
            {data?.chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={Color['brand.tertiary.lightest']}
                fillOpacity={selectedIndex === undefined || selectedIndex === index ? 1 : 0.6}
              />
            ))}
          </Bar>
          <Area
            type="monotone"
            dataKey="netFundraisingValue"
            stroke={Color['feedback.success.default']}
            fill="url(#assetCaptureFill)"
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }, [
    loading,
    error,
    data?.chartData,
    data?.total,
    barCategoryGap,
    isMobile,
    nameMap,
    refresher,
    handleSelectIndex,
    selectedIndex,
  ]);

  if (isMobile) {
    return (
      <SectionContainer
        title={chartTitle}
        multipleFilterLabel={isMobile ? 'Período' : 'Filtre por período'}
        filterOptions={filterOptions}
        selectedOption={filterOptions[0].value}
        withFilter
        paddingX={Spacing['stack.xxxs']}>
        {chart}
      </SectionContainer>
    );
  }

  return (
    <SectionContainer
      title={chartTitle}
      filterOptions={filterOptions}
      selectedOption={filterOptions[0].value}
      withFilter
      withSeparator
      afterFilterText={loading ? '' : `${data?.startDateNormalized} até ${data?.endDateNormalized}`}
      paddingX={Spacing['stack.xxxs']}>
      {chart}
    </SectionContainer>
  );
};
